import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: 'website' | 'article';
}

const SITE_URL = 'https://www.lauramartinfitness.co.uk';
const DEFAULT_OG_IMAGE = '/images/Laura_1.png';

function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let tag = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

function toAbsoluteUrl(urlOrPath: string) {
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  const path = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
  return `${SITE_URL}${path}`;
}

export function useSEO({ title, description, canonical, noindex = false, ogImage = DEFAULT_OG_IMAGE, ogType = 'website' }: SEOProps) {
  useEffect(() => {
    document.title = title;

    const defaultCanonical = `${SITE_URL}${window.location.pathname === '/' ? '' : window.location.pathname}`;
    const canonicalUrl = canonical ? toAbsoluteUrl(canonical) : defaultCanonical;
    const ogImageUrl = toAbsoluteUrl(ogImage);

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', ogImageUrl);
    setMeta('property', 'og:image:alt', 'Laura Martin Fitness personal training and coaching');
    setMeta('property', 'og:locale', 'en_GB');
    setMeta('property', 'og:site_name', 'Laura Martin Fitness');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImageUrl);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }, [title, description, canonical, noindex, ogImage, ogType]);
}
